/**
 * Frontend WebSocket Hook
 * 
 * Connects to the backend WS endpoint and dispatches real-time events.
 * Automatically reconnects with exponential backoff on disconnection.
 * 
 * Usage:
 *   const { lastEvent, isConnected, sendMessage } = useWebSocket();
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from './authStore';

export interface WSEvent {
  type: string;
  data: any;
  timestamp: string;
}

export type WSEventHandler = (event: WSEvent) => void;

interface UseWebSocketOptions {
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
  /** Max reconnect attempts before giving up (default: 10) */
  maxReconnectAttempts?: number;
  /** Which event types to listen for (default: all) */
  eventFilter?: string[];
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function resolveWsBase(): string {
  if (typeof window === 'undefined') {
    return 'ws://localhost:3001';
  }

  const runtimeWsUrl = typeof (window as any).__VITE_WS_URL__ === 'string'
    ? String((window as any).__VITE_WS_URL__).trim()
    : '';
  if (runtimeWsUrl) {
    return trimTrailingSlash(runtimeWsUrl);
  }

  const envWsUrl = typeof (import.meta as any).env?.VITE_WS_URL === 'string'
    ? String((import.meta as any).env.VITE_WS_URL).trim()
    : '';
  if (envWsUrl) {
    return trimTrailingSlash(envWsUrl);
  }

  const envApiUrl = typeof (import.meta as any).env?.VITE_API_URL === 'string'
    ? String((import.meta as any).env.VITE_API_URL).trim()
    : '';
  if (envApiUrl && /^https?:\/\//i.test(envApiUrl)) {
    try {
      const apiUrl = new URL(envApiUrl);
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${apiUrl.host}`;
    } catch {
      // Fall through to host-based inference.
    }
  }

  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalDev) {
    const devPort = (import.meta as any).env?.VITE_API_PORT || '3001';
    return `${wsProtocol}//${window.location.hostname}:${devPort}`;
  }

  // Hosted environment default: use same host/port as the current page.
  return `${wsProtocol}//${window.location.host}`;
}

function buildWsUrl(token: string): string {
  const base = trimTrailingSlash(resolveWsBase());
  const withPath = /\/ws$/i.test(base) ? base : `${base}/ws`;
  return `${withPath}?token=${encodeURIComponent(token)}`;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { autoConnect = true, maxReconnectAttempts = 10, eventFilter } = options;
  const authToken = useAuthStore((s) => s.token);

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WSEvent | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlersRef = useRef<Map<string, Set<WSEventHandler>>>(new Map());
  const mountedRef = useRef(true);
  const lastPongRef = useRef<number>(Date.now());
  const reconnectCountRef = useRef(0);

  /** Register a handler for a specific event type */
  const on = useCallback((eventType: string, handler: WSEventHandler) => {
    if (!handlersRef.current.has(eventType)) {
      handlersRef.current.set(eventType, new Set());
    }
    handlersRef.current.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      handlersRef.current.get(eventType)?.delete(handler);
    };
  }, []);

  /** Send a message to the server */
  const sendMessage = useCallback((type: string, data: any = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, data }));
    }
  }, []);

  /** Connect to WebSocket server */
  const connect = useCallback(() => {
    const token = authToken;
    if (!token) {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      reconnectCountRef.current = 0;
      setReconnectCount(0);
      setIsConnected(false);
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const url = buildWsUrl(token);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setIsConnected(true);
      reconnectCountRef.current = 0;
      setReconnectCount(0);
      console.log('[WS] Connected to Scrudix real-time server');
    };

    ws.onmessage = (messageEvent) => {
      if (!mountedRef.current) return;
      try {
        const event: WSEvent = JSON.parse(messageEvent.data);

        // Track pong heartbeat responses
        if (event.type === 'pong') {
          lastPongRef.current = Date.now();
          return;
        }

        // Apply event filter if specified
        if (eventFilter && !eventFilter.includes(event.type)) return;

        setLastEvent(event);

        // Dispatch to registered handlers
        const handlers = handlersRef.current.get(event.type);
        if (handlers) {
          for (const handler of handlers) {
            try {
              handler(event);
            } catch (err) {
              console.error(`[WS] Handler error for ${event.type}:`, err);
            }
          }
        }

        // Also dispatch to wildcard '*' handlers
        const wildcardHandlers = handlersRef.current.get('*');
        if (wildcardHandlers) {
          for (const handler of wildcardHandlers) {
            try {
              handler(event);
            } catch (err) {
              console.error('[WS] Wildcard handler error:', err);
            }
          }
        }
      } catch {
        // Non-JSON message, ignore
      }
    };

    ws.onclose = (closeEvent) => {
      if (!mountedRef.current) return;
      setIsConnected(false);

      // Don't reconnect if closed normally
      if (closeEvent.code === 1000) {
        return;
      }

      // Exponential backoff reconnect
      if (reconnectCountRef.current < maxReconnectAttempts) {
        const nextAttempt = reconnectCountRef.current + 1;
        const isAuthRelatedClose = closeEvent.code === 4001 || closeEvent.code === 4003;
        const delay = isAuthRelatedClose
          ? 4_000
          : Math.min(1000 * Math.pow(2, reconnectCountRef.current), 30_000);

        console.log(`[WS] Reconnecting in ${delay}ms (attempt ${nextAttempt}/${maxReconnectAttempts})`);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
        }
        reconnectTimer.current = setTimeout(() => {
          if (!mountedRef.current) return;
          reconnectCountRef.current = nextAttempt;
          setReconnectCount(nextAttempt);
          connect();
        }, delay);
      } else {
        console.warn('[WS] Max reconnect attempts reached');
      }
    };

    ws.onerror = () => {
      // onclose will fire after this, handling reconnect
    };
  }, [authToken, maxReconnectAttempts, eventFilter]);

  /** Disconnect from WebSocket server */
  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    mountedRef.current = true;
    if (autoConnect && authToken) {
      connect();
    } else {
      disconnect();
    }
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [autoConnect, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat: ping every 25s and detect stale connection (no pong in 35s)
  useEffect(() => {
    if (!isConnected) return;
    lastPongRef.current = Date.now();
    const interval = setInterval(() => {
      sendMessage('ping');
      // If no pong received in 35s, force reconnect
      if (Date.now() - lastPongRef.current > 35_000) {
        console.warn('[WS] Heartbeat timeout — forcing reconnect');
        wsRef.current?.close(4000, 'Heartbeat timeout');
      }
    }, 25_000);
    return () => clearInterval(interval);
  }, [isConnected, sendMessage]);

  // Reconnect when page becomes visible again (tab switch / wake from sleep)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected && mountedRef.current) {
        console.log('[WS] Page visible — reconnecting');
        reconnectCountRef.current = 0;
        setReconnectCount(0);
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isConnected, connect]);

  return {
    isConnected,
    lastEvent,
    reconnectCount,
    on,
    sendMessage,
    connect,
    disconnect,
  };
}
